import React from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    Grid,
    CircularProgress,
    Alert,
    Autocomplete,
    TextField,
    InputAdornment,
} from '@mui/material';
import {
    Search as SearchIcon,
    FileDownload as DownloadIcon,
    ArrowBack as ArrowBackIcon,
    Print as PrintIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useResidents } from '../hooks/useResidents';
import { useTenant } from '../contexts/TenantContext';
import OrderMedicationService from '../services/OrderMedicationService';
import { ResidentDTO } from '../types/resident.types';
import { format } from 'date-fns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

const DownloadMarPage: React.FC = () => {
    const navigate = useNavigate();
    const { currentTenantId } = useTenant();
    const { residents, loading: residentsLoading, fetchActiveLookup } = useResidents();

    const [selectedResident, setSelectedResident] = React.useState<ResidentDTO | null>(null);
    const [inputValue, setInputValue] = React.useState('');
    const [startDate, setStartDate] = React.useState<Date | null>(null);
    const [endDate, setEndDate] = React.useState<Date | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [successMessage, setSuccessMessage] = React.useState<string | null>(null);

    // Load active residents once on mount
    React.useEffect(() => {
        if (currentTenantId) {
            fetchActiveLookup(currentTenantId);
        }
    }, [currentTenantId, fetchActiveLookup]);


    // Auto-dismiss success message
    React.useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => {
                setSuccessMessage(null);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);

    React.useEffect(() => {
        if (error) {
            const timer = setTimeout(() => {
                setError(null);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [error]);

    const handleDownloadMAR = async () => {
        if (!selectedResident || !selectedResident.id) {
            setError('Please select a resident');
            return;
        }

        const formattedStartDate = startDate ? format(startDate, 'dd/MM/yyyy') : undefined;
        const formattedEndDate = endDate ? format(endDate, 'dd/MM/yyyy') : undefined;

        try {
            setLoading(true);
            setError(null);
            setSuccessMessage(null);

            // Pre-check: Verify if the resident has any medication orders
            const orders = await OrderMedicationService.getOrdersByResident(selectedResident.id);
            if (!orders || orders.length === 0) {
                setError('No medication records found for the specified resident');
                setLoading(false);
                return;
            }

            const blob = await OrderMedicationService.generateMAR(
                selectedResident.id,
                formattedStartDate,
                formattedEndDate
            );

            const url = window.URL.createObjectURL(blob);

            // Create an invisible iframe for printing
            const iframe = document.createElement("iframe");
            iframe.style.visibility = "hidden";
            iframe.style.position = "fixed";
            iframe.style.right = "0";
            iframe.style.bottom = "0";
            iframe.style.width = "1px";
            iframe.style.height = "1px";
            iframe.style.border = "0";
            iframe.src = url;
            document.body.appendChild(iframe);

            iframe.onload = () => {
                try {
                    if (iframe.contentWindow) {
                        iframe.contentWindow.focus();
                        iframe.contentWindow.print();
                    }
                } catch (printErr) {
                    console.error("Direct print failed:", printErr);
                    // Fallback to opening in new tab if iframe print fails
                    window.open(url, '_blank');
                }

                // Cleanup
                setTimeout(() => {
                    if (document.body.contains(iframe)) document.body.removeChild(iframe);
                    window.URL.revokeObjectURL(url);
                }, 60000); // Keep for a minute to allow print dialog to finish
            };

            setSuccessMessage(`MAR for ${selectedResident.firstName} ${selectedResident.lastName} generated successfully`);
        } catch (err: any) {
            setError(err.message || 'Failed to download MAR');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ width: '100%', maxWidth: '100%', px: 2, pb: 2, pt: 3, }}>
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                mb: 3,
                gap: { xs: 1, sm: 2 }
            }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(-1)}
                    sx={{ color: '#48CAE4', textTransform: 'none' }}
                >
                    Back
                </Button>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#48CAE4' }}>
                    Download MAR
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {successMessage && (
                <Alert severity="success" sx={{ mb: 3 }}>
                    {successMessage}
                </Alert>
            )}

            <Paper sx={{ p: { xs: 2, md: 4 }, borderRadius: '16px', border: '1px solid #e0e0e0' }}>
                <Grid container spacing={4}>
                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom sx={{ color: '#434445' }}>
                            Select Resident
                        </Typography>
                        <Autocomplete
                            options={residents.filter(r => r.status === "ACTIVE").sort((a, b) => {
                                const nameA = (a.residentName || `${a.firstName} ${a.lastName}`).toLowerCase();
                                const nameB = (b.residentName || `${b.firstName} ${b.lastName}`).toLowerCase();
                                return nameA.localeCompare(nameB);
                            })}
                            getOptionLabel={(option) =>
                                `${(option as any).residentName || `${option.firstName} ${option.lastName}`} ${option.roomNumber ? `(Room: ${option.roomNumber})` : ""}`
                            }

                            loading={residentsLoading}
                            value={selectedResident}
                            onChange={(event, newValue) => {
                                setSelectedResident(newValue);
                                setError(null);
                            }}
                            inputValue={inputValue}
                            onInputChange={(event, newInputValue) => {
                                setInputValue(newInputValue);
                            }}

                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Search Resident..."
                                    variant="outlined"
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon color="action" />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <React.Fragment>
                                                {residentsLoading ? (
                                                    <CircularProgress color="inherit" size={20} />
                                                ) : null}
                                                {params.InputProps.endAdornment}
                                            </React.Fragment>
                                        ),
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '12px',
                                            backgroundColor: '#f5f7f8',
                                        },
                                    }}
                                />
                            )}
                            fullWidth
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Typography variant="h6" gutterBottom sx={{ color: '#434445' }}>
                            From Date
                        </Typography>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                format="dd/MM/yyyy"
                                value={startDate}
                                onChange={(newValue) => {
                                    setStartDate(newValue);
                                    if (newValue && endDate && endDate < newValue) {
                                        setEndDate(newValue);
                                    }
                                }}
                                slotProps={{
                                    actionBar: {
                                        actions: ['clear']
                                    },
                                    textField: {
                                        fullWidth: true,
                                        sx: {
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '12px',
                                                backgroundColor: '#f5f7f8',
                                            },
                                        },
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Typography variant="h6" gutterBottom sx={{ color: '#434445' }}>
                            To Date
                        </Typography>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                format="dd/MM/yyyy"
                                value={endDate}
                                minDate={startDate || undefined}
                                onChange={(newValue) => {
                                    if (newValue && startDate && newValue < startDate) {
                                        setEndDate(startDate);
                                    } else {
                                        setEndDate(newValue);
                                    }
                                }}
                                slotProps={{
                                    actionBar: {
                                        actions: ['clear']
                                    },
                                    textField: {
                                        fullWidth: true,
                                        sx: {
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '12px',
                                                backgroundColor: '#f5f7f8',
                                            },
                                        },
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>

                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PrintIcon />}
                            onClick={handleDownloadMAR}
                            disabled={loading || !selectedResident}
                            sx={{
                                bgcolor: '#48CAE4',
                                color: '#000000',
                                textTransform: 'none',
                                px: 6,
                                py: 1.5,
                                borderRadius: '10px',
                                width: { xs: '100%', sm: 'auto' },
                                fontSize: '1.1rem',
                                fontWeight: 'normal',
                                '&:hover': {
                                    bgcolor: '#3ab3cc',
                                },
                                '&.Mui-disabled': {
                                    bgcolor: '#ccc',
                                }
                            }}
                        >
                            {loading ? 'Generating MAR...' : 'Print MAR'}
                        </Button>
                    </Grid>
                </Grid>
            </Paper>
        </Box>
    );
};

export default DownloadMarPage;